import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, Tag, X, Plus, BadgePercent, Star, FileText } from 'lucide-react'
import { getReservationSettings, getOptionSettings, checkCoupon } from '../../lib/api/booking'
import { useBooking } from '../../store/bookingStore'
import { usePrefillBookingFromQuery } from '../../lib/prefillFromQuery'
import { useCalculatedPrice } from '../../lib/useCalculatedPrice'
import { StepShell } from '../../components/layout/StepShell'
import { PriceSummary } from '../../components/layout/PriceSummary'
import { ErrorState } from '../../components/ui/States'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { formatCents } from '../../lib/money'
import { stepPath } from '../../lib/steps'
import type { Locale, RatePublic, ServicePublic } from '../../lib/api/types'
import { cn } from '../../lib/cn'

export function RateStep() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const couponLabel = useBooking((s) => s.couponLabel)
  const setRate = useBooking((s) => s.setRate)
  const toggleService = useBooking((s) => s.toggleService)
  const setConvention = useBooking((s) => s.setConvention)
  const setCoupon = useBooking((s) => s.setCoupon)
  const clearStaleDates = useBooking((s) => s.clearStaleDates)
  useCalculatedPrice() // keeps the price aside + breakdown live

  // External entry: the marketing-site booking bar links straight here with the selection in the
  // URL (?tid=&from=&time=&to=). Hydrate the funnel store from those params before the guard runs.
  const prefillReady = usePrefillBookingFromQuery()

  // guard: need dates first, and they must still be in the future — a tab left open overnight would
  // otherwise post yesterday's arrival day and get rejected on every retry
  useEffect(() => {
    if (!prefillReady) return
    const stale = clearStaleDates()
    if (stale || !cart.transportId || !cart.fromDay || !cart.fromHour)
      navigate(stepPath(routeLocale, 'dates'), { replace: true })
  }, [prefillReady, cart.transportId, cart.fromDay, cart.fromHour, clearStaleDates, navigate, routeLocale])

  // rates for the chosen period
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reservationSettings', cart.transportId, cart.fromDay, cart.fromHour, cart.toDay],
    queryFn: () => getReservationSettings(cart.transportId!, cart.fromDay!, cart.fromHour!, cart.toDay ?? ''),
    enabled: Boolean(cart.transportId && cart.fromDay && cart.fromHour),
  })

  // services + conventions + coupon applicability (same step, no extra page — matches legacy)
  const options = useQuery({
    queryKey: ['optionSettings', cart.transportId, cart.fromDay, cart.fromHour, cart.rateId],
    queryFn: () => getOptionSettings(cart),
    enabled: Boolean(cart.transportId && cart.fromDay && cart.fromHour),
  })

  const rates = data?.rateList ?? []
  const cheapestId = useMemo(() => {
    if (!rates.length) return null
    return [...rates].sort(
      (a, b) => (a.calculateAmount?.discountedAmount ?? 1e9) - (b.calculateAmount?.discountedAmount ?? 1e9),
    )[0]?.id
  }, [rates])

  // auto-select if only one rate
  useEffect(() => {
    if (rates.length === 1 && cart.rateId == null) setRate(rates[0].id)
  }, [rates, cart.rateId, setRate])

  // When the backend exposes no selectable rates it still computes the price via
  // an automatically-applied rate (mainCalculation). The legacy app skips the
  // selection in that case; we show the applied rate and let the user continue.
  const mainCalc = data?.mainCalculation
  const notBookable = !isLoading && rates.length === 0 && !mainCalc

  const services = options.data?.serviceList ?? []
  const conventions = options.data?.conventionList ?? []
  const selected = new Set(cart.serviceIdList ?? [])

  // coupon ui
  const [code, setCode] = useState('')
  const [couponError, setCouponError] = useState(false)
  const [checking, setChecking] = useState(false)
  async function applyCoupon() {
    if (!code.trim() || !cart.transportId || !cart.fromDay) return
    setChecking(true)
    setCouponError(false)
    try {
      const res = await checkCoupon(cart.transportId, cart.fromDay, code.trim())
      if (res.isApplicable && res.coupon) {
        setCoupon(res.coupon.code, res.coupon.code)
        setCode('')
      } else {
        setCouponError(true)
      }
    } catch {
      setCouponError(true)
    } finally {
      setChecking(false)
    }
  }

  return (
    <StepShell
      stepKey="rate"
      title={t('rate.title')}
      subtitle={t('rate.subtitle')}
      onBack={() => navigate(stepPath(routeLocale, 'dates'))}
      onPrimary={() => navigate(stepPath(routeLocale, 'info'))}
      primaryDisabled={rates.length > 0 ? cart.rateId == null : !mainCalc}
    >
      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        <div className="space-y-7">
          {/* rate selection — when the cheapest is auto-applied (no choice), show nothing here */}
          {(isLoading || rates.length > 0 || notBookable) && (
            <section className="space-y-3">
              {isLoading ? (
                [0, 1].map((i) => <RateCardSkeleton key={i} />)
              ) : rates.length > 0 ? (
                rates.map((rate) => (
                  <RateCard
                    key={rate.id}
                    rate={rate}
                    locale={locale}
                    selected={cart.rateId === rate.id}
                    best={rate.id === cheapestId && rates.length > 1}
                    onSelect={() => setRate(rate.id)}
                  />
                ))
              ) : notBookable ? (
                <div className="gsm-card p-8 text-center text-sm text-ink-500">{t('rate.noRates')}</div>
              ) : null}
            </section>
          )}

          {/* The rest of the step renders only once the rate query has settled — otherwise
              real data (services / convention / price) appears beneath the rate skeletons. */}
          {!isLoading && (
            <>
          {/* extra services (same step as the rate) */}
          {services.length > 0 && (
            <section className="space-y-3">
              <label className="gsm-label">{t('services.title')}</label>
              {services.map((s) => (
                <ServiceCard key={s.id} service={s} locale={locale} selected={selected.has(s.id)} onToggle={() => toggleService(s.id)} />
              ))}
            </section>
          )}

          {/* convention — highlighted card (gives 10% off) so it's clearly visible */}
          {conventions.length > 0 && (
            <section className="rounded-2xl bg-white p-4 shadow-card ring-1 ring-ink-100 sm:p-5">
              <div className="flex items-start gap-3.5">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <BadgePercent className="h-[22px] w-[22px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h3 className="font-display text-[16px] font-extrabold tracking-tight text-ink-900">
                      {t('services.convention')}
                    </h3>
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-bold text-brand-700">−10%</span>
                  </div>
                  <p className="mt-0.5 text-[13px] leading-snug text-ink-500">{t('services.conventionHint')}</p>
                  <div className="mt-3">
                    <Select
                      ariaLabel={t('services.convention')}
                      value={String(cart.conventionId ?? '')}
                      placeholder={t('services.conventionNone')}
                      onChange={(v) => setConvention(v ? Number(v) : null, conventions.find((c) => String(c.id) === v)?.name ?? null)}
                      options={[
                        { value: '', label: t('services.conventionNone') },
                        ...conventions.map((c) => ({ value: String(c.id), label: c.name, description: c.description })),
                      ]}
                    />
                  </div>
                  {cart.conventionId != null && (
                    <p className="mt-2.5 flex items-start gap-1.5 text-[13px] font-medium text-brand-600">
                      <FileText className="mt-px h-3.5 w-3.5 flex-none" />
                      {t('services.conventionDocs')}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* coupon */}
          {(options.data?.hasCouponApplicable || cart.couponCode) && (
            <section>
              <label className="gsm-label flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> {t('services.coupon')}
              </label>
              {cart.couponCode ? (
                <div className="flex items-center justify-between rounded-xl bg-brand-50 px-4 py-3 ring-1 ring-brand-100 sm:max-w-md">
                  <span className="flex items-center gap-2 text-sm font-semibold text-brand-700">
                    <Check className="h-4 w-4" /> {couponLabel ?? cart.couponCode} · {t('services.couponApplied')}
                  </span>
                  <button onClick={() => setCoupon(null, null)} className="text-brand-600 hover:text-brand-800">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 sm:max-w-md">
                  <input
                    className={cn('gsm-input', couponError && 'border-brand-500')}
                    placeholder={t('services.couponPlaceholder')}
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value)
                      setCouponError(false)
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                  />
                  <Button variant="outline" onClick={applyCoupon} loading={checking} className="flex-none">
                    {t('services.apply')}
                  </Button>
                </div>
              )}
              {couponError && <p className="mt-1 text-xs font-medium text-brand-600">{t('services.couponInvalid')}</p>}
            </section>
          )}

          {/* live price breakdown — so the client sees how rate/services/convention/coupon change the total */}
          <div className="lg:hidden">
            <PriceSummary />
          </div>
            </>
          )}
        </div>
      )}
    </StepShell>
  )
}

// Loading placeholder shaped like a real rate card (radio + title + description + price),
// so it doesn't read as a blank grey box floating in empty space.
function RateCardSkeleton() {
  return (
    <div className="rounded-2xl border-2 border-ink-100 bg-white p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <div className="skeleton h-6 w-6 rounded-full" />
            <div className="skeleton h-5 w-28 rounded-md" />
          </div>
          <div className="mt-3 space-y-2 pl-[2.125rem]">
            <div className="skeleton h-3 w-full rounded" />
            <div className="skeleton h-3 w-2/3 rounded" />
          </div>
        </div>
        <div className="flex flex-none flex-col items-end gap-1.5">
          <div className="skeleton h-7 w-20 rounded-md" />
          <div className="skeleton h-3 w-12 rounded" />
        </div>
      </div>
    </div>
  )
}

function RateCard({
  rate,
  locale,
  selected,
  best,
  onSelect,
}: {
  rate: RatePublic
  locale: Locale
  selected: boolean
  best: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const price = rate.calculateAmount?.discountedAmount
  const original = rate.calculateAmount?.fullOriginalPrice
  const discounted = original != null && price != null && original > price
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'group relative block w-full overflow-hidden rounded-2xl border-2 bg-white p-5 text-left transition-all sm:p-6',
        selected
          ? 'border-brand-600 shadow-card-hover ring-1 ring-brand-600'
          : 'border-ink-100 hover:border-ink-200 hover:shadow-card',
      )}
    >
      {best && (
        <span className="absolute left-0 top-0 inline-flex items-center gap-1 rounded-br-xl rounded-tl-xl bg-brand-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          <Star className="h-3 w-3 fill-current" /> {t('rate.best')}
        </span>
      )}
      <div className={cn('flex items-start justify-between gap-4', best && 'pt-5')}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <span
              className={cn(
                'flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 transition',
                selected
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-ink-200 group-hover:border-ink-300',
              )}
            >
              {selected && <Check className="h-3.5 w-3.5" />}
            </span>
            <h3 className="min-w-0 truncate font-display text-lg font-extrabold text-ink-900 sm:text-xl">
              {rate.label}
            </h3>
          </div>
          {rate.description && (
            <div
              className="mt-2 line-clamp-3 pl-[2.125rem] text-sm leading-relaxed text-ink-500 [&_p]:m-0 [&_p+p]:mt-1"
              dangerouslySetInnerHTML={{ __html: rate.description }}
            />
          )}
        </div>
        <div className="flex flex-none flex-col items-end text-right">
          {discounted && (
            <span className="text-xs text-ink-300 line-through tabular-nums">{formatCents(original, locale)}</span>
          )}
          <span className="font-display text-2xl font-extrabold tabular-nums text-ink-900 sm:text-3xl">
            {price != null ? formatCents(price, locale) : '—'}
          </span>
          <span className="mt-0.5 text-[11px] font-medium text-ink-400">{t('rate.perStay')}</span>
        </div>
      </div>
      {selected && (
        <div className="mt-4 flex items-center gap-1.5 border-t border-ink-100 pt-3 text-[11px] font-bold uppercase tracking-wide text-brand-700">
          <Check className="h-3.5 w-3.5" /> {t('common.selected')}
        </div>
      )}
    </button>
  )
}

function ServiceCard({
  service,
  locale,
  selected,
  onToggle,
}: {
  service: ServicePublic
  locale: Locale
  selected: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-4 rounded-2xl border-2 bg-white p-4 text-left transition-all',
        selected ? 'border-brand-600 shadow-card-hover' : 'border-ink-100 hover:border-ink-200 hover:shadow-card',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 flex-none items-center justify-center rounded-xl transition',
          selected ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-400',
        )}
      >
        {selected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-ink-900">{service.label}</h3>
        {service.description && (
          <div className="line-clamp-1 text-sm text-ink-400 [&_p]:m-0" dangerouslySetInnerHTML={{ __html: service.description }} />
        )}
      </div>
      <span className="flex-none font-display font-bold text-ink-900">+ {formatCents(service.price, locale)}</span>
    </button>
  )
}
